import { LoadingButton } from '@mui/lab'
import React, { useEffect, useState } from 'react'
import { useNotify, useShowController, useTranslate } from 'react-admin'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard'
import { billingServices } from 'src/services/reservation/billing/billing.service'
import RefreshIcon from '@mui/icons-material/Refresh';
import { Collapse, IconButton, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, DialogTitle, Grid } from '@mui/material'
import { StatusTableCell } from '../../helper/StatusTableCell'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { parseDateOnlyFormat } from 'src/helper/parseDateOnlyFormat'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PDFRenderer from 'src/components/uiElements/fields/PDFRenderer'
import CloseIcon from '@mui/icons-material/Close';
import CachedIcon from '@mui/icons-material/Cached';
import { InvalidateDocument } from './modals/InvalidateDocument'
import { exportTable } from './logic/ExportTable'
import { HasPermission } from 'src/helper/HasPermission'
import { CurrencyAmount } from 'src/components/uiElements/fields/CurrencyAmountChip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'

export const CommercialDocs = ({ documentsRefreshToken }) => {
    const {record} = useShowController()
    const notify = useNotify()
    const translate = useTranslate()
    const [documents, setDocuments] = useState([])
    const [paginatedDocuments, setPaginatedDocuments] = useState([])
    const [loadingDocuments, setLoadingDocuments] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const [itemsSelected, setItemsSelected] = useState([])
    const [fileToShow, setFileToShow] = useState(null)
    const [dataToShow, setDataToShow] = useState()
    const [openInvalidate, setOpenInvalidate] = useState(false)
    const [idToInvalidate, setIdToInvalidate] = useState(null)
    const [isRefetching, setIsRefetching] = useState(null)
    const [paginationData, setPaginationData] = useState({ page: 0, rowsPerPage: 10 })
    const [isExpanded, setIsExpanded] = useState(true)

    useEffect(()=>{
        getDocuments()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[documentsRefreshToken])

    useEffect(()=>{
        initializePagination()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[paginationData, documents])

    const initializePagination = () =>{
        const paginatedData = documents.slice(paginationData.page*paginationData.rowsPerPage, paginationData.page*paginationData.rowsPerPage + paginationData.rowsPerPage)
        setPaginatedDocuments(paginatedData)
    }

    const onPageChange = (number) => {
        const paginationDataAux = { ...paginationData }
        paginationDataAux.page = number
        setPaginationData(paginationDataAux)
    }

    const onRowsPerPageChange = (e) => {
        const paginationDataAux = { ...paginationData }
        paginationDataAux.rowsPerPage = parseInt(e.target.value, 10)
        paginationDataAux.page = 0
        setPaginationData(paginationDataAux)
    }

    const getDocuments = async() =>{
        setLoadingDocuments(true)
        await billingServices.getDocuments(record?.id).then((res)=>{
            setDocuments(res)
        }).finally(()=>{
            setLoadingDocuments(false)
        })
    }

    const refetchDocument = async (invoice_id) =>{
        setIsRefetching(invoice_id)
        await billingServices.refetchDocument(invoice_id).then(async ()=>{
            await getDocuments()
        }).catch(()=>{
            notify('ra.notification.refetch_data_error',{type:'error'})
        }).finally(()=>{
            setIsRefetching(null)
        })
    }

    const handleDownloadFile = (item) => {
        if(item.url){
            const link = document.createElement('a')
            link.href = item.url
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(item.url)
        }
    }

    const onExport = () =>{
        const titles = [
            translate('ra.field.type'),
            translate('ra.field.number'),
            translate('ra.field.billing_base'),
            translate('ra.field.status'),
            translate('ra.field.amount'),
            translate('ra.field.currency'),
            translate('ra.field.date_emision'),
            translate('ra.field.payment_policy')
        ]
        const data = paginatedDocuments.map(doc=>[
            translate(`ra.field.${doc.type?.toLowerCase()}`),
            doc.nro,
            doc.base,
            translate(`ra.status.${doc.status}`),
            numberFormatterWithDecimalAndThousand(doc.amount),
            doc.currency_relation?.short_name,
            parseDateOnlyFormat(doc.invoice_date),
            doc.payment_policy

        ])
        exportTable(titles, data)
    }

  return (
    <ShowCard
        title={translate('ra.title.commercial_documents')}
        posTitle={
            <IconButton
                size='small'
                onClick={()=>setIsExpanded(prev=>!prev)}
                style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                }}
            >
                <ExpandMoreIcon />
            </IconButton>
        }
        preActionButtonJustify='flex-end'
        preActionButton={
            <>
                <LoadingButton  onClick={()=>getDocuments()}>
                    <RefreshIcon />
                </LoadingButton>
                <DownloadIcon style={{cursor:'pointer'}} color='info' onClick={onExport}/>
            </>
        }
    >
        <Collapse in={isExpanded} unmountOnExit style={{width:'100%'}}>
            <TableContainer style={{width:'100%'}}>
                <Table>
                    <TableHead>
                        <TableRow style={{backgroundColor:'#f5f5f5'}}>
                            <TableCell>
                                <Checkbox
                                    checked={!!selectAll}
                                    onChange={(val)=>{
                                        const checked = val.target.checked
                                        if(checked){
                                            setItemsSelected(documents.map((item)=>item.id))
                                        } else {
                                            setItemsSelected([])
                                        }
                                        setSelectAll(prev=>!prev)
                                    }}
                                />
                            </TableCell>
                            <TableCell>{translate('ra.field.type')}</TableCell>
                            <TableCell>{translate('ra.field.number')}</TableCell>
                            <TableCell>{translate('ra.field.billing_base')}</TableCell>
                            <TableCell align='center'>{translate('ra.field.status')}</TableCell>
                            <TableCell align='right'>{translate('ra.field.amount')}</TableCell>
                            <TableCell align='center'>{translate('ra.field.date_emision')}</TableCell>
                            <TableCell>{translate('ra.field.payment_policy')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            loadingDocuments ? (
                                <TableRow>
                                    {[...Array(7)].map((_, i) => (
                                    <TableCell key={i}>
                                        <Skeleton variant='rectangular' width={'100%'} height={20} />
                                    </TableCell>
                                    ))}
                                </TableRow>
                            ) : (
                                paginatedDocuments.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Checkbox
                                                checked = {itemsSelected.includes(item.id)}
                                                onChange={(val)=>{
                                                    const checked = val.target.checked
                                                    let auxItems = [...itemsSelected]
                                                    if(checked){
                                                        auxItems.push(item.id)
                                                    } else {
                                                        auxItems = auxItems.filter(auxItem=>auxItem !== item.id)
                                                    }
                                                    setItemsSelected(auxItems)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{translate(`ra.field.${item.type?.toLowerCase()}`)}</TableCell>
                                        <TableCell>{item.nro}</TableCell>
                                        <TableCell>{item.base}</TableCell>
                                        <StatusTableCell align='center' data={item.status} inlineStatus/>
                                        <TableCell align='right'>
                                            <CurrencyAmount currency={item.currency_relation?.short_name} amount={item.amount} />
                                        </TableCell>
                                        <TableCell align='center'>{parseDateOnlyFormat(item.invoice_date)}</TableCell>
                                        <TableCell>{item.payment_policy}</TableCell>
                                        <TableCell>
                                            <div style={{display:'flex', flexDirection:'row', gap:'3px'}}>
                                                <HasPermission resource={'invoice'} permission='get'>
                                                    <Tooltip title={translate('ra.action.see_something',{thing:'PDF'})}>
                                                        <InsertDriveFileIcon
                                                            color={item.url ? 'info' :'disabled'}
                                                            style={{cursor:item.url ? 'pointer' : 'inherit'}}
                                                            onClick={() => setFileToShow(item.url)}
                                                        />
                                                    </Tooltip>
                                                </HasPermission>
                                                <HasPermission resource={'invoice'} permission='get'>
                                                    <Tooltip title={`${translate('ra.action.download')} PDF`}>
                                                        <DownloadIcon
                                                            color={item.url ? 'info' :'disabled'}
                                                            style={{cursor:item.url ? 'pointer' : 'inherit'}}
                                                            onClick={() => handleDownloadFile(item)}
                                                        />
                                                    </Tooltip>
                                                </HasPermission>
                                                <HasPermission resource={'invoice'} permission='show'>
                                                    <Tooltip title={`${translate('ra.action.see_something', {thing:`${translate('ra.field.document')}`})}`}>
                                                        <VisibilityIcon
                                                            color='info'
                                                            style={{cursor:'pointer'}}
                                                            onClick={() => setDataToShow(item)}
                                                        />
                                                    </Tooltip>
                                                </HasPermission>
                                                <HasPermission resource={'invoice'} permission='edit'>
                                                    <Tooltip title={`${translate('ra.action.refresh_data')}`}>
                                                        {isRefetching === item.id ?
                                                            <CircularProgress size={'15px'}/>
                                                        :
                                                            <CachedIcon color='info' style={{cursor:'pointer'}} onClick={()=>refetchDocument(item.id)}/>
                                                        }
                                                    </Tooltip>
                                                </HasPermission>
                                                <HasPermission resource={'invoice'} permission='create'>
                                                    <Tooltip title={`${translate('ra.action.invalidate')} ${translate('ra.field.document')}`}>
                                                        <CloseIcon 
                                                            color={item.status === 'INVOICED' ? 'error' :'disabled'}
                                                            style={{cursor:item.status === 'INVOICED' ?'pointer':'inherit'}} 
                                                            onClick={() => {
                                                                if(item.status === 'INVOICED' ){
                                                                    setOpenInvalidate(true)

                                                                    setIdToInvalidate(item.id)
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </HasPermission>
                                            </div>
                                        </TableCell>

                                    </TableRow>
                                ))
                            )
                        }
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                colSpan={12}
                                count={documents?.length}
                                rowsPerPage={paginationData.rowsPerPage}
                                page={paginationData.page}
                                onPageChange={(e, number) => onPageChange(number)}
                                onRowsPerPageChange={(e) => onRowsPerPageChange(e)}
                                labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                                labelDisplayedRows={({ from, to, count }) =>
                                `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                                }
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>

            <Dialog open={fileToShow} onClose={() => setFileToShow(null)}>
                <DialogContent>
                    <PDFRenderer url={fileToShow} alt={`Vista previa PDF`} />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        onClick={() => setFileToShow(null)}
                    >
                        {translate('ra.action.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dataToShow} onClose={() => setDataToShow(null)} fullWidth='md'>
                <DialogTitle>
                    {translate('ra.title.document_data')}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        <ShowListElement sm={6} label={translate('ra.field.change_type')} value={dataToShow?.exchange_rate} />
                        <ShowListElement sm={6} label={translate('ra.field.fiscal_number')} value={dataToShow?.fiscal_number} />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        onClick={() => setDataToShow(null)}
                    >
                        {translate('ra.action.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <InvalidateDocument
                openInvalidate={openInvalidate}
                setOpenInvalidate={setOpenInvalidate}
                idToInvalidate={idToInvalidate}
                getDocuments={getDocuments}
            />
        </Collapse>
    </ShowCard>
  )
}